import React, { useEffect } from 'react';

import { Box, Grid, Typography } from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { Tooltip } from 'common/components';
import {
    ReactHookFormInput,
    ReactHookFormIntlNumberInput,
    ReactHookFormSelect,
} from 'common/components/form/bootstrap';
import { getPolygonHeadings } from 'common/components/segment/asceHelpers';
import { POLYGON_RECTANGLE } from 'common/components/segment/constants';
import Azimuth from 'common/resources/Azimuth.svg?react';

import createProposalModal from '../../../../createProposalModal';

import PanelsOrKwInput from './PanelsOrKwInput';

const TabModule = ({
    control,
    google,
    fetchPanels,
    panelBrands,
    panels,
    setValue,
    systemUid,
}) => {
    const { t } = useTranslation();
    const { getPanelBrandsOptions, getPanelsOptions } =
        createProposalModal.helpers;
    const [azimuth_selector, brandName, drawing_type, polygon, sloof_type] =
        useWatch({
            control,
            name: [
                'azimuth_selector',
                'brandName',
                'drawing_type',
                'polygon',
                'sloof_type',
            ],
        });

    const isCustomModule = brandName === 'custom';
    const panelBrandsOptions = [
        ...getPanelBrandsOptions(panelBrands),
        { label: t('Custom'), value: 'custom' },
    ];

    const polygonHeadings = google
        ? getPolygonHeadings({
              google,
              polygonCoords: polygon,
          })
        : [];

    const buildingHeadingOptions = polygonHeadings.map(({ heading }) => ({
        label: `${heading}°`,
        value: `${heading}`,
    }));

    const showAzimuthSelector =
        drawing_type !== POLYGON_RECTANGLE || sloof_type === '';

    buildingHeadingOptions.push({
        label: t('Custom'),
        value: 'custom',
    });

    const onChangeBrand = (event) => {
        const brandName = event.target.value || '';
        setValue('solar_module', '');
        setValue('panel', {
            depth: 0,
            height: 0,
            watts: 0,
            weight: 0,
            width: 0,
        });
        if (!isEmpty(brandName) && brandName !== 'custom') {
            setValue('is_custom_module', false);
        } else if (brandName === 'custom') {
            setValue('is_custom_module', true);
            setValue('panel', {
                brandName: t('Custom'),
                color: '#F0CA90',
                depth: 35,
                height: 1600,
                is_custom: true,
                name: t('Custom'),
                stroke_color: '#0047ba',
                watts: 225,
                weight: 20,
                width: 960,
            });
        }
    };

    const onChangeSolarModule = (event) => {
        const solar_module = event.target.value || '';
        if (!isEmpty(solar_module) && Array.isArray(panels) && panels.length) {
            const selectedModule = panels.find(
                (panel) => panel.id == solar_module,
            );

            if (selectedModule) {
                setValue('selectedModule', selectedModule);
                setValue('panel', selectedModule);
            }
        }
    };

    useEffect(() => {
        if (brandName && brandName !== 'custom') {
            fetchPanels(brandName);
        }
    }, [brandName]);

    const getIsDisabledOrientation = () => {
        if (
            [
                'ARCK-CUBIERTA GRAFADA',
                'ARCK-TEJA BARRO',
                'ARCK-TRAPEZOIDAL',
                'ARCK-TRAPEZOIDAL CLAMP',
                'ARCK-TRAPEZOIDAL ALT',
            ].includes(systemUid)
        )
            return false;
        return true;
    };

    return (
        <>
            <ReactHookFormSelect
                control={control}
                isClearable={false}
                label={t('Panel brand')}
                name="brandName"
                onChange={onChangeBrand}
                options={panelBrandsOptions}
            />

            {!isCustomModule && (
                <ReactHookFormSelect
                    autoComplete="off"
                    control={control}
                    disabled={isEmpty(brandName)}
                    label={t('Panel')}
                    name="solar_module"
                    onChange={onChangeSolarModule}
                    options={getPanelsOptions(panels)}
                />
            )}

            {isCustomModule && (
                <ReactHookFormInput
                    control={control}
                    fullWidth
                    label={t('Model')}
                    name="panel.name"
                />
            )}

            <Grid container>
                <Grid size="grow">
                    <ReactHookFormIntlNumberInput
                        append="mm"
                        control={control}
                        disabled={!isCustomModule}
                        fullWidth
                        label={t('Width')}
                        name="panel.width"
                        placeholder={t('Width')}
                    />
                </Grid>

                <Grid size="grow">
                    <ReactHookFormIntlNumberInput
                        append="mm"
                        control={control}
                        disabled={!isCustomModule}
                        fullWidth
                        label={t('Length')}
                        name="panel.height"
                        placeholder={t('Length')}
                    />
                </Grid>
            </Grid>

            <Grid container>
                <Grid size="grow">
                    <ReactHookFormIntlNumberInput
                        append="mm"
                        control={control}
                        disabled={!isCustomModule}
                        fullWidth
                        label={t('Depth')}
                        name="panel.depth"
                        placeholder={t('Depth')}
                    />
                </Grid>

                <Grid size="grow">
                    <ReactHookFormIntlNumberInput
                        append="Kg"
                        control={control}
                        disabled={!isCustomModule}
                        fullWidth
                        label={t('Weight')}
                        name="panel.weight"
                        placeholder={t('Weight')}
                    />
                </Grid>
            </Grid>

            <ReactHookFormIntlNumberInput
                append="w"
                control={control}
                disabled={!isCustomModule}
                fullWidth
                label={t('Power')}
                min="0"
                name="panel.watts"
                placeholder={t('Power')}
            />

            <PanelsOrKwInput
                control={control}
                offerPanels={panels}
                setValue={setValue}
            />

            <ReactHookFormSelect
                control={control}
                disabled={getIsDisabledOrientation()}
                fullWidth
                label={t('Orientation')}
                name="orientation"
                options={[
                    { label: t('Portrait'), value: 'P' },
                    { label: t('Landscape'), value: 'L' },
                ]}
            />

            {showAzimuthSelector && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <ReactHookFormSelect
                        control={control}
                        label={t('Azimuth')}
                        name="azimuth_selector"
                        options={buildingHeadingOptions}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value !== 'custom') setValue('azimuth', value);
                        }}
                    />

                    <Box>
                        <Tooltip
                            sx={{
                                maxWidth: '400px',
                                maxHeight: '400px',
                                p: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <Azimuth
                                style={{
                                    width: '15em',
                                    height: 'auto',
                                }}
                            />
                            <Typography variant="body2">
                                {t("Panel's alignment in relation to the sun")}
                            </Typography>
                        </Tooltip>
                    </Box>
                </Box>
            )}

            {azimuth_selector === 'custom' && (
                <ReactHookFormIntlNumberInput
                    append="°"
                    control={control}
                    fullWidth
                    label={t('Azimuth')}
                    max="360"
                    min="0"
                    name="azimuth"
                    placeholder={t('Azimuth')}
                />
            )}
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    panelBrands: createProposalModal.selectors.getPanelBrandsData,
    panels: createProposalModal.selectors.getPanelsData,
});

const mapDispatchToProps = (dispatch) => ({
    fetchPanels: (brandId) =>
        dispatch(createProposalModal.actions.fetchPanels(brandId)),
});

TabModule.propTypes = {
    control: PropTypes.object,
    fetchPanels: PropTypes.func,
    google: PropTypes.object,
    panelBrands: PropTypes.array,
    panels: PropTypes.array,
    setValue: PropTypes.func,
    systemUid: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(TabModule);
